// /catalog/items — lista de favorite_items del partner activo, con filtros
// por tipo + archivados. Pagination keyset por (name, favorite_id).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  deleteFavoriteItem,
  listFavoriteItems,
  listFavoriteTypes,
  patchFavoriteItem,
  type FavoriteItem,
} from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { CatalogReadOnlyBanner, useCatalogContext } from '@/components/CatalogShell';
import ConfirmDialog from '@/components/ConfirmDialog';
import { fieldCls, checkboxCls } from '@/lib/form-styles';
import { canWrite as canWriteSection } from '@/lib/access';

export default function CatalogItems(): JSX.Element {
  const { partnerId } = useCatalogContext();
  const session = useSession();
  const canWrite = canWriteSection(session, 'catalog');
  const qc = useQueryClient();

  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showArchived, setShowArchived] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FavoriteItem | null>(null);
  const [accumulated, setAccumulated] = useState<FavoriteItem[]>([]);

  const typesQ = useQuery({
    queryKey: ['admin-favorite-types', partnerId],
    queryFn: () => listFavoriteTypes(partnerId),
    enabled: !!partnerId,
  });

  const listKey = ['admin-favorite-items', partnerId, typeFilter, showArchived, cursor] as const;
  const itemsQ = useQuery({
    queryKey: listKey,
    queryFn: () =>
      listFavoriteItems(partnerId, {
        ...(typeFilter ? { favorite_type_id: typeFilter } : {}),
        ...(showArchived ? {} : { status: 'active' as const }),
        ...(cursor ? { cursor } : {}),
        limit: 25,
      }),
    enabled: !!partnerId,
  });

  // Mantener un array acumulado para soportar "Cargar mas". Cuando cambian
  // filtros o tipos, lo reseteamos.
  if (itemsQ.data && cursor === null && accumulated.length > 0 && accumulated[0]?.partner_id !== partnerId) {
    setAccumulated([]);
  }
  // Cuando llega una pagina nueva con cursor != null, la appendeamos.
  // (Se ejecuta una vez por mount del componente con cada cursor; React garantiza
  // estabilidad — chequeamos por longitud para evitar duplicacion en re-renders.)

  function resetAndApplyFilters(next: { typeFilter?: string; showArchived?: boolean }): void {
    if (next.typeFilter !== undefined) setTypeFilter(next.typeFilter);
    if (next.showArchived !== undefined) setShowArchived(next.showArchived);
    setCursor(null);
    setAccumulated([]);
  }

  const archive = useMutation({
    mutationFn: (id: string) => deleteFavoriteItem(id),
    onSuccess: () => {
      setConfirmDelete(null);
      setAccumulated([]);
      setCursor(null);
      qc.invalidateQueries({ queryKey: ['admin-favorite-items', partnerId] });
    },
  });

  const restore = useMutation({
    mutationFn: (id: string) => patchFavoriteItem(id, { status: 'active' }),
    onSuccess: () => {
      setAccumulated([]);
      setCursor(null);
      qc.invalidateQueries({ queryKey: ['admin-favorite-items', partnerId] });
    },
  });

  const items = cursor === null ? itemsQ.data?.items ?? [] : [...accumulated, ...(itemsQ.data?.items ?? [])];
  const nextCursor = itemsQ.data?.next_cursor ?? null;

  function loadMore(): void {
    if (!itemsQ.data || !nextCursor) return;
    setAccumulated((prev) => [...prev, ...itemsQ.data.items]);
    setCursor(nextCursor);
  }

  const types = typesQ.data ?? [];
  const typeNameById = new Map(types.map((t) => [t.favorite_type_id, t.name] as const));

  return (
    <div className="space-y-4">
      <CatalogReadOnlyBanner canWrite={canWrite} />

      <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-3 items-end">
          <label className="text-sm space-y-1">
            <span className="font-medium">Tipo</span>
            <select
              value={typeFilter}
              onChange={(e) => resetAndApplyFilters({ typeFilter: e.target.value })}
              className={fieldCls({ size: 'lg' })}
            >
              <option value="">Todos los tipos</option>
              {types.map((t) => (
                <option key={t.favorite_type_id} value={t.favorite_type_id}>
                  {t.name} ({t.code})
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm flex items-center gap-2 mt-6 md:mt-0">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => resetAndApplyFilters({ showArchived: e.target.checked })}
              className={checkboxCls()}
            />
            Mostrar archivados
          </label>
          <Link
            to="/catalog/items/new"
            className={`min-h-[2.75rem] inline-flex items-center rounded-lg bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 self-end ${
              canWrite ? '' : 'pointer-events-none opacity-50'
            }`}
            aria-disabled={!canWrite}
          >
            Nuevo item
          </Link>
        </div>
      </section>

      {itemsQ.isLoading && items.length === 0 ? (
        <p className="text-sm text-ink-muted">Cargando…</p>
      ) : null}
      {itemsQ.isError ? (
        <div role="alert" className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error">
          {itemsQ.error instanceof AdminApiError ? itemsQ.error.message : 'No pudimos cargar los items.'}
        </div>
      ) : null}

      {items.length > 0 ? (
        <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                  <th className="text-left px-5 py-2">Nombre</th>
                  <th className="text-left px-5 py-2">Code</th>
                  <th className="text-left px-5 py-2">Tipo</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-right px-5 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr
                    key={i.favorite_id}
                    className="border-b border-surface-border dark:border-surface-dark-border last:border-b-0"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/catalog/items/${encodeURIComponent(i.favorite_id)}`}
                        className="font-medium hover:underline"
                      >
                        {i.name}
                      </Link>
                      {i.description ? (
                        <p className="text-xs text-ink-muted line-clamp-1">{i.description}</p>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{i.code}</td>
                    <td className="px-5 py-3 text-xs">
                      {typeNameById.get(i.favorite_type_id) ?? (
                        <span className="font-mono text-ink-muted">{i.favorite_type_id}</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`text-xs rounded-full px-2 py-0.5 font-medium ${
                          i.status === 'active'
                            ? 'bg-status-success/20 text-status-success'
                            : 'bg-ink-muted/20 text-ink-muted'
                        }`}
                      >
                        {i.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right space-x-3">
                      {i.status === 'archived' ? (
                        <button
                          type="button"
                          onClick={() => restore.mutate(i.favorite_id)}
                          disabled={!canWrite || restore.isPending}
                          className="text-sm text-brand hover:underline disabled:opacity-50"
                        >
                          Restaurar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(i)}
                          disabled={!canWrite}
                          className="text-sm text-status-error hover:underline disabled:opacity-50"
                        >
                          Archivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor ? (
            <div className="border-t border-surface-border dark:border-surface-dark-border p-3 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={itemsQ.isFetching}
                className="text-sm text-brand hover:underline disabled:opacity-50"
              >
                {itemsQ.isFetching ? 'Cargando…' : 'Cargar mas'}
              </button>
            </div>
          ) : null}
        </section>
      ) : itemsQ.data && !itemsQ.isLoading ? (
        <p className="text-sm text-ink-muted">Sin items para los filtros actuales.</p>
      ) : null}

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Archivar item"
        description={
          confirmDelete ? (
            <p>
              Se va a archivar <strong>{confirmDelete.name}</strong>. Los usuarios que lo
              siguen lo seguiran viendo en su lista con flag <code>archived</code>. Podes
              restaurarlo cuando quieras.
            </p>
          ) : null
        }
        confirmLabel="Archivar"
        danger
        loading={archive.isPending}
        onConfirm={() => confirmDelete && archive.mutate(confirmDelete.favorite_id)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
